import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import {
  type RealTimeEvent,
  useRealTimeUpdates,
  type UseRealTimeUpdatesReturn,
} from '~/components/AiChat/hooks/useRealTimeUpdates';
import type { IssueTokenResponse } from '~/components/AiChat/types';
import type {
  StpNotification,
  StpNotificationMetadata,
} from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import { notificationsFirestoreService } from '~/services/firestore/firestoreService';

interface ConductorHubTokenApiResponse {
  issue_token_response: IssueTokenResponse | null;
}

export interface UseConductorHubNotificationsOptions {
  /** Internal claim id, used to fetch the real-time token. */
  claimId: number;
  /** When false, no token is fetched and nothing is subscribed. */
  enabled?: boolean;
  /** How many of the most recent notifications to read; the service default when omitted. */
  eventLimit?: number;
  /**
   * Keep every notification once it has loaded, and read earlier history on request (see
   * `useRealTimeUpdates`). Off for the feed, which shows the newest window.
   */
  keepHistory?: boolean;
}

export interface UseConductorHubNotificationsReturn
  extends Pick<UseRealTimeUpdatesReturn, 'hasEarlier' | 'loadEarlier' | 'loadSince'> {
  notifications: StpNotification[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Each event's notification, built once. `useRealTimeUpdates` creates an event once per subscription and
 * hands the same object back in every snapshot, so keying on it keeps a notification's identity while its
 * event is loaded, which is what lets a memoized consumer skip a snapshot that did not touch it. The entry
 * goes when the event does.
 */
const notificationByEvent = new WeakMap<RealTimeEvent, StpNotification>();

const toNotification = (event: RealTimeEvent): StpNotification => {
  const cached = notificationByEvent.get(event);
  if (cached) return cached;

  const notification: StpNotification = {
    event_id: event.event_id,
    account_id: '',
    claim_number: '',
    topic: 'stp',
    title: event.title ?? null,
    message: event.message ?? null,
    level: event.level,
    timestamp: event.timestamp,
    metadata: (event.metadata as StpNotificationMetadata | undefined) ?? undefined,
  };
  notificationByEvent.set(event, notification);

  return notification;
};

/**
 * Live source of the Conductor Hub's conductor stream.
 *
 * 1. Fetches an account/claim/topic-scoped real-time token from the CMS backend
 *    (`GET /api/v1/claims/{id}/conductor-hub/token`), which proxies the fivesigma_ai
 *    `get_claim_notification_realtime_token` call.
 * 2. Subscribes to Firestore via the shared {@link useRealTimeUpdates} hook — the token's
 *    `firestore_config` (claims-llm / rtu_events) and `query_config` (account_id + claim_number
 *    + topic == 'stp') drive the connection and filtering server-side.
 * 3. Maps each real-time event to {@link StpNotification} for the feed.
 */
export const useConductorHubNotifications = ({
  claimId,
  enabled = true,
  eventLimit,
  keepHistory = false,
}: UseConductorHubNotificationsOptions): UseConductorHubNotificationsReturn => {
  const [issueTokenResponse, setIssueTokenResponse] = useState<IssueTokenResponse | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  // Whether the token request is still in flight. Tracked separately from `issueTokenResponse`
  // so a request that resolves with no token doesn't leave us loading forever.
  const [isTokenLoading, setIsTokenLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let aborted = false;
    setIsTokenLoading(true);
    setTokenError(null);
    // Drop the previous claim's token immediately so we tear down its subscription and
    // surface a loading state, instead of showing stale notifications until the new token arrives.
    setIssueTokenResponse(null);

    axios
      .get<ConductorHubTokenApiResponse>(`/api/v1/claims/${claimId}/conductor-hub/token`)
      .then(({ data }) => {
        if (!aborted) {
          setIssueTokenResponse(data.issue_token_response ?? null);
          if (!data.issue_token_response) {
            // A resolved-but-empty token is a stream that never connected, and an empty feed is a
            // claim that nothing happened on this claim. Only an error tells those apart: without
            // this the request settles with no token, no error and nothing loading, and the hub
            // renders its empty state for a subscription it never opened.
            setTokenError('No real-time token was issued for this claim');
          }
        }
      })
      .catch((err: unknown) => {
        if (!aborted) {
          setTokenError(err instanceof Error ? err.message : 'Failed to load Conductor Hub notifications');
        }
      })
      .finally(() => {
        if (!aborted) {
          setIsTokenLoading(false);
        }
      });

    return () => {
      aborted = true;
    };
  }, [claimId, enabled]);

  const {
    events,
    hasReceivedInitialSnapshot,
    error: subscriptionError,
    hasEarlier,
    loadEarlier,
    loadSince,
  } = useRealTimeUpdates({
    issueTokenResponse,
    enabled: enabled && Boolean(issueTokenResponse),
    // Dedicated Firestore instance (separate Firebase app + auth) so the conductor stream
    // never disconnects or re-authenticates the shared chat connection.
    service: notificationsFirestoreService,
    eventLimit,
    keepHistory,
  });

  // `events` is state and only changes when a snapshot lands, so this array keeps its identity
  // across unrelated re-renders. Callers memoize on `notifications` — a fresh array here would
  // recompute every sort and join they hang off it on every render.
  const notifications: StpNotification[] = useMemo(() => events.map(toNotification), [events]);

  const error = tokenError ?? subscriptionError;
  const isConnecting = Boolean(issueTokenResponse) && !hasReceivedInitialSnapshot && !error;

  return {
    notifications,
    isLoading: isTokenLoading || isConnecting,
    error,
    hasEarlier,
    loadEarlier,
    loadSince,
  };
};
